// Bounded publishing/feed-mix/winning-ticket policy
// (/organizations/:id/personalized-bets/policy). Source configuration
// (provider/source_mode/persona) is never part of this shape; `enabled` exists
// only inside winning_tickets, where it switches card generation.
// `winning_tickets.category_visible` travels with the policy but is stored on
// the Winning Tickets surface, which the API reads back into every response.

export const POLICY_QUERY_KEY = 'personalized-bets-policy'

export const DEFAULT_POLICY = Object.freeze({
  publishing: Object.freeze({
    ticket_posts_enabled: true,
    recommendation_posts_enabled: true,
    max_active_ticket_posts: 20,
    max_active_recommendation_posts: 20,
    max_new_ticket_posts_per_hour: 10,
  }),
  ranking: Object.freeze({
    mode: 'global_with_follows',
    target_personalized_first_10: 3,
    max_consecutive_personalized: 1,
    max_per_creator_first_20: 3,
    max_per_sport_first_20: 5,
    max_per_event_first_20: 2,
    follow_boost: 2,
    version: 'global_follow_v2',
  }),
  winning_tickets: Object.freeze({
    category_visible: false,
    enabled: false,
    synthetic_enabled: true,
    show_in_main_feed: true,
    lead_main_feed: false,
    team_colors: false,
    max_active: 10,
    max_new_per_hour: 3,
    max_first_10: 1,
    min_posts_between: 3,
    shelf_life_hours: 48,
    min_legs: 1,
    max_legs: 6,
  }),
})

// Policy sections in payload and change-summary order.
export const POLICY_SECTIONS = Object.freeze(['publishing', 'ranking', 'winning_tickets'])

export const POLICY_BOUNDS = Object.freeze({
  max_active_ticket_posts: [0, 100],
  max_active_recommendation_posts: [0, 100],
  max_new_ticket_posts_per_hour: [0, 50],
  target_personalized_first_10: [0, 5],
  max_consecutive_personalized: [1, 3],
  max_per_creator_first_20: [1, 10],
  max_per_sport_first_20: [1, 15],
  max_per_event_first_20: [1, 10],
  follow_boost: [1, 3],
  max_active: [0, 50],
  max_new_per_hour: [0, 20],
  max_first_10: [0, 3],
  min_posts_between: [0, 20],
  shelf_life_hours: [6, 168],
  min_legs: [1, 10],
  max_legs: [1, 10],
})

export const POLICY_LABELS = Object.freeze({
  ticket_posts_enabled: 'Ticket posts',
  recommendation_posts_enabled: 'Crowd recommendations',
  max_active_ticket_posts: 'Maximum active ticket posts',
  max_active_recommendation_posts: 'Maximum active recommendations',
  max_new_ticket_posts_per_hour: 'Maximum new ticket posts per hour',
  target_personalized_first_10: 'Personalized bets in first 10',
  max_consecutive_personalized: 'Maximum in a row',
  max_per_creator_first_20: 'Maximum per creator in first 20',
  max_per_sport_first_20: 'Maximum per sport in first 20',
  max_per_event_first_20: 'Maximum per event in first 20',
  follow_boost: 'Followed creator boost',
  category_visible: 'Winning Tickets category',
  enabled: 'Winning tickets',
  synthetic_enabled: 'Combinations from settled selections',
  show_in_main_feed: 'Winning tickets in the main feed',
  lead_main_feed: 'Newest winning ticket leads the main feed',
  team_colors: 'Winning team colors on cards',
  max_active: 'Maximum active winning tickets',
  max_new_per_hour: 'Maximum new winning tickets per hour',
  max_first_10: 'Maximum winning tickets in first 10',
  min_posts_between: 'Minimum posts between winning tickets',
  shelf_life_hours: 'Winning ticket shelf life (hours)',
  min_legs: 'Minimum legs',
  max_legs: 'Maximum legs in combinations',
})

export const INTEGER_POLICY_FIELDS = new Set(Object.keys(POLICY_BOUNDS).filter((field) => field !== 'follow_boost'))

// [field, step] pairs in render order.
export const PERSONALIZED_TICKET_FIELDS = Object.freeze([
  ['max_active_ticket_posts', 1],
  ['max_new_ticket_posts_per_hour', 1],
])

export const CROWD_RECOMMENDATION_FIELDS = Object.freeze([
  ['max_active_recommendation_posts', 1],
])

export const FEED_MIX_FIELDS = Object.freeze([
  ['target_personalized_first_10', 1],
  ['max_consecutive_personalized', 1],
  ['max_per_creator_first_20', 1],
  ['max_per_sport_first_20', 1],
  ['max_per_event_first_20', 1],
  ['follow_boost', 0.1],
])

export const WINNING_TICKET_FIELDS = Object.freeze([
  ['max_active', 1],
  ['max_new_per_hour', 1],
  ['max_first_10', 1],
  ['min_posts_between', 1],
  ['shelf_life_hours', 1],
  ['min_legs', 1],
  ['max_legs', 1],
])

// Chooser order. `policySection` is the policy block `switches`/`fields` edit;
// `feedMix` types also show the shared ranking (Feed mix) fields in their rail;
// `kind` is the appearance endpoint slug, `brandingGroup` its
// components.<group>, `contentOrigin` the feed origin the widget styles with it.
export const GENERATED_POST_TYPES = Object.freeze([
  Object.freeze({
    value: 'winning_ticket',
    label: 'Winning tickets',
    kind: 'winning-ticket',
    brandingGroup: 'winningTicket',
    contentOrigin: 'winning_ticket',
    policySection: 'winning_tickets',
    description: 'Publish cards for selections that settled as winners. Winning tickets never appear back-to-back, '
      + 'and past the first 10 they keep at least the minimum number of other posts between them. '
      + 'The first-10 number is a ceiling, not a guarantee: a card earns a place there on score, '
      + 'unless the newest card leads the feed. '
      + 'Minimum legs applies to every card; maximum legs only to combinations built from settled selections, '
      + 'so a creator\'s long winner still gets its card.',
    switches: Object.freeze([
      {
        field: 'enabled',
        label: 'Publish winning tickets',
        description: 'Cards post from the feed\'s own WinningTickets account rather than a creator. A card is withdrawn if its win is resettled. Off takes every live card down as soon as you save; on brings back the ones that have not expired.',
      },
      {
        field: 'category_visible',
        label: 'Show the Winning Tickets category',
        description: 'Off hides the Winning Tickets surface: the feed refuses it and the app no longer lists the category. Cards keep publishing and return with it.',
      },
      {
        field: 'synthetic_enabled',
        label: 'Generate combinations from settled selections',
        description: 'Off limits cards to creator posts that won; those keep a Follow button for the creator.',
      },
      {
        field: 'show_in_main_feed',
        label: 'Show in the main feed',
        description: 'Cards always reach the Winning Tickets surface; this also mixes them into default surfaces. Saving a change moves the live cards at once.',
      },
      {
        field: 'lead_main_feed',
        label: 'Lead the main feed with the newest winning ticket',
        description: 'Off, winning tickets compete for position like any post. On, the newest card opens the main feed for every viewer, after pinned posts. Needs a first-10 maximum of at least 1.',
      },
      {
        field: 'team_colors',
        label: 'Color cards with the winning team\'s colors',
        description: 'New cards whose legs all back one NFL, college football (FBS), NBA, WNBA, MLB or NHL team take that team\'s colors; other cards keep the appearance below. Live cards are not recolored: match or reset a card\'s colors from its row in Feed Ops.',
      },
    ]),
    fields: WINNING_TICKET_FIELDS,
  }),
  Object.freeze({
    value: 'crowd_recommendation',
    label: 'AI generated',
    kind: 'crowd-recommendation',
    brandingGroup: 'crowdRecommendation',
    contentOrigin: 'personalized_recommendation',
    policySection: 'publishing',
    description: 'Crowd recommendations the feed generates from betting activity. They count toward the personalized share set under Feed mix.',
    switches: Object.freeze([
      { field: 'recommendation_posts_enabled', label: 'Publish crowd recommendations' },
    ]),
    fields: CROWD_RECOMMENDATION_FIELDS,
    feedMix: true,
  }),
  Object.freeze({
    value: 'personalized_ticket',
    label: 'Users generated',
    kind: 'personalized-ticket',
    brandingGroup: 'personalizedTicket',
    contentOrigin: 'personalized_ticket',
    policySection: 'publishing',
    description: 'Ticket posts generated from bets placed by the feed\'s users. They count toward the personalized share set under Feed mix.',
    switches: Object.freeze([
      {
        field: 'ticket_posts_enabled',
        label: 'Publish ticket posts',
        description: 'Setting the active limit to zero pauses new ticket posts.',
      },
    ]),
    fields: PERSONALIZED_TICKET_FIELDS,
    feedMix: true,
  }),
])

export const DEFAULT_GENERATED_POST_TYPE = GENERATED_POST_TYPES[0].value

export const GENERATED_POST_TYPE_PARAM = 'type'

// Unknown values fall back to the first type.
export function generatedPostType(value) {
  return GENERATED_POST_TYPES.find((type) => type.value === value) || GENERATED_POST_TYPES[0]
}

export function normalizePolicy(response) {
  const policy = response?.policy || response || {}
  return {
    publishing: {
      ...DEFAULT_POLICY.publishing,
      ...(policy.publishing || {}),
    },
    ranking: {
      ...DEFAULT_POLICY.ranking,
      ...(policy.ranking || {}),
      mode: 'global_with_follows',
      version: 'global_follow_v2',
    },
    winning_tickets: {
      ...DEFAULT_POLICY.winning_tickets,
      ...(policy.winning_tickets || {}),
    },
  }
}

function winningTicketsPayload(section) {
  return {
    category_visible: Boolean(section.category_visible),
    enabled: Boolean(section.enabled),
    synthetic_enabled: Boolean(section.synthetic_enabled),
    show_in_main_feed: Boolean(section.show_in_main_feed),
    lead_main_feed: Boolean(section.lead_main_feed),
    team_colors: Boolean(section.team_colors),
    ...Object.fromEntries(WINNING_TICKET_FIELDS.map(([field]) => [field, Number(section[field])])),
  }
}

function sectionChanged(previous, next) {
  return Object.entries(next).some(([field, value]) => String(previous?.[field]) !== String(value))
}

// The winning-tickets section travels only when it changed against saved, so
// a publishing edit never rewrites it.
export function policyPayload(policy, saved) {
  const payload = {
    publishing: {
      ticket_posts_enabled: Boolean(policy.publishing.ticket_posts_enabled),
      recommendation_posts_enabled: Boolean(policy.publishing.recommendation_posts_enabled),
      max_active_ticket_posts: Number(policy.publishing.max_active_ticket_posts),
      max_active_recommendation_posts: Number(policy.publishing.max_active_recommendation_posts),
      max_new_ticket_posts_per_hour: Number(policy.publishing.max_new_ticket_posts_per_hour),
    },
    ranking: {
      mode: 'global_with_follows',
      target_personalized_first_10: Number(policy.ranking.target_personalized_first_10),
      max_consecutive_personalized: Number(policy.ranking.max_consecutive_personalized),
      max_per_creator_first_20: Number(policy.ranking.max_per_creator_first_20),
      max_per_sport_first_20: Number(policy.ranking.max_per_sport_first_20),
      max_per_event_first_20: Number(policy.ranking.max_per_event_first_20),
      follow_boost: Number(policy.ranking.follow_boost),
      version: 'global_follow_v2',
    },
  }
  if (policy.winning_tickets && (!saved?.winning_tickets || sectionChanged(saved.winning_tickets, policy.winning_tickets))) {
    payload.winning_tickets = winningTicketsPayload(policy.winning_tickets)
  }
  return payload
}

function policyGroupOf(policy, field) {
  return POLICY_SECTIONS.find((group) => policy[group] && Object.hasOwn(policy[group], field))
}

export function validatePolicy(policy) {
  const errors = {}
  for (const [field, [minimum, maximum]] of Object.entries(POLICY_BOUNDS)) {
    const group = policyGroupOf(policy, field)
    if (!group) continue
    const rawValue = policy[group][field]
    const value = Number(rawValue)
    if (rawValue === '' || rawValue === null || rawValue === undefined) {
      errors[field] = `${POLICY_LABELS[field]} is required.`
      continue
    }
    if (!Number.isFinite(value) || (INTEGER_POLICY_FIELDS.has(field) && !Number.isInteger(value))) {
      errors[field] = INTEGER_POLICY_FIELDS.has(field)
        ? `${POLICY_LABELS[field]} must be a whole number.`
        : `${POLICY_LABELS[field]} must be a number.`
      continue
    }
    if (value < minimum || value > maximum) {
      errors[field] = `${POLICY_LABELS[field]} must be between ${minimum} and ${maximum}.`
    }
  }
  const legs = policy.winning_tickets
  if (legs && !errors.min_legs && !errors.max_legs && Number(legs.min_legs) > Number(legs.max_legs)) {
    errors.max_legs = `${POLICY_LABELS.max_legs} must be at least ${POLICY_LABELS.min_legs.toLowerCase()}.`
  }
  return errors
}

export function policyChanges(previous, next) {
  if (!previous || !next) return []
  const changes = []
  for (const group of POLICY_SECTIONS) {
    if (!previous[group] || !next[group]) continue
    for (const [field, value] of Object.entries(next[group])) {
      if (field === 'mode' || field === 'version') continue
      if (String(previous[group][field]) === String(value)) continue
      const before = typeof previous[group][field] === 'boolean' ? (previous[group][field] ? 'on' : 'off') : previous[group][field]
      const after = typeof value === 'boolean' ? (value ? 'on' : 'off') : value
      changes.push(`${POLICY_LABELS[field]}: ${before} → ${after}`)
    }
  }
  return changes
}
